import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';

import { fetchAuthSession } from '../../auth/gateway';
import { API_CONFIG } from '../../config/api';
import { secureLogger } from '../../utils/secureLogger';
import {
  DEFAULT_TRACKINTEL_DIST_THRESHOLD_M,
  DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN,
  DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_ENABLED,
  DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN,
  DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN,
} from './analysisConfig';
import { buildLocationAnalysisTimelineItems } from './timelineBuilder';
import type {
  DerivedLocation,
  DerivedStaypoint,
  DerivedTimelineTarget,
  DerivedTrip,
  DerivedTripleg,
  POICandidate,
  POIEnrichment,
  RebuildJob,
  TrackintelRunParameters,
} from './timelineTypes';

type UseLocationAnalysisDerivedDataOptions = {
  deviceId?: string | null;
  rangeStartIso?: string | null;
  rangeEndIso?: string | null;
  autoLoad?: boolean;
};

const buildDefaultTrackintelParameters = (): TrackintelRunParameters => ({
  distThresholdM: DEFAULT_TRACKINTEL_DIST_THRESHOLD_M,
  timeThresholdMin: DEFAULT_TRACKINTEL_TIME_THRESHOLD_MIN,
  gapThresholdMin: DEFAULT_TRACKINTEL_GAP_THRESHOLD_MIN,
  mergeStaypointsEnabled: DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_ENABLED,
  mergeStaypointsMaxTimeGapMin: DEFAULT_TRACKINTEL_MERGE_STAYPOINTS_MAX_TIME_GAP_MIN,
});

export const useLocationAnalysisDerivedData = ({
  deviceId,
  rangeStartIso,
  rangeEndIso,
  autoLoad = true,
}: UseLocationAnalysisDerivedDataOptions) => {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [locations, setLocations] = useState<DerivedLocation[]>([]);
  const [visibleLocations, setVisibleLocations] = useState<DerivedLocation[]>([]);
  const [staypoints, setStaypoints] = useState<DerivedStaypoint[]>([]);
  const [triplegs, setTriplegs] = useState<DerivedTripleg[]>([]);
  const [trips, setTrips] = useState<DerivedTrip[]>([]);
  const [selectedDerivedTarget, setSelectedDerivedTarget] = useState<DerivedTimelineTarget | null>(null);
  const [poiCandidatesByLocationId, setPoiCandidatesByLocationId] = useState<Map<string, POICandidate[]>>(new Map());
  const [searchingPoiLocationId, setSearchingPoiLocationId] = useState<string | null>(null);
  const [linkingPoiLocationId, setLinkingPoiLocationId] = useState<string | null>(null);
  const [rebuildJob, setRebuildJob] = useState<RebuildJob | null>(null);
  const poiSearchCacheRef = useRef<Map<string, POICandidate[]>>(new Map());
  const poiSearchRequestIdByLocationRef = useRef<Map<string, number>>(new Map());

  const fetchToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('認証トークンが取得できませんでした');
    }
    return token;
  }, []);

  const clearData = useCallback(() => {
    setLocations([]);
    setVisibleLocations([]);
    setStaypoints([]);
    setTriplegs([]);
    setTrips([]);
    setSelectedDerivedTarget(null);
    setPoiCandidatesByLocationId(new Map());
    setRebuildJob(null);
  }, []);

  const loadDerivedData = useCallback(async () => {
    if (!deviceId || !rangeStartIso || !rangeEndIso) {
      clearData();
      return;
    }
    setLoading(true);
    try {
      const token = await fetchToken();
      const params = new URLSearchParams({
        range_start: rangeStartIso,
        range_end: rangeEndIso,
        device_id: deviceId,
      });
      const [locationsResponse, staypointsResponse, triplegsResponse, tripsResponse] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations?include_inactive=true`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/staypoints?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/trips?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!locationsResponse.ok) {
        throw new Error(`locations API failed: ${locationsResponse.status}`);
      }
      if (!staypointsResponse.ok) {
        throw new Error(`staypoints API failed: ${staypointsResponse.status}`);
      }
      if (!triplegsResponse.ok) {
        throw new Error(`triplegs API failed: ${triplegsResponse.status}`);
      }
      if (!tripsResponse.ok) {
        throw new Error(`trips API failed: ${tripsResponse.status}`);
      }

      const [locationsPayload, staypointsPayload, triplegsPayload, tripsPayload] = await Promise.all([
        locationsResponse.json(),
        staypointsResponse.json(),
        triplegsResponse.json(),
        tripsResponse.json(),
      ]);
      const allLocations = locationsPayload as DerivedLocation[];

      const locationIdsInRange = new Set<string>(
        (staypointsPayload as DerivedStaypoint[])
          .map((staypoint) => staypoint.effective_location_id)
          .filter((value): value is string => typeof value === 'string' && value.length > 0)
      );
      const filteredLocations = allLocations.filter((location) =>
        locationIdsInRange.has(location.id)
      );

      setLocations(allLocations);
      setVisibleLocations(filteredLocations);
      setStaypoints(staypointsPayload);
      setTriplegs(triplegsPayload);
      setTrips(tripsPayload);
      setPoiCandidatesByLocationId(new Map());
    } catch (error) {
      secureLogger.error('Failed to load location analysis derived data', {
        error: error instanceof Error ? error.message : 'unknown',
      });
      message.error(error instanceof Error ? error.message : 'LocationAnalysis データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [clearData, deviceId, fetchToken, rangeEndIso, rangeStartIso]);

  useEffect(() => {
    if (autoLoad) {
      void loadDerivedData();
    }
  }, [autoLoad, loadDerivedData]);

  useEffect(() => {
    setRebuildJob(null);
  }, [deviceId, rangeEndIso, rangeStartIso]);

  const runAnalysis = useCallback(async (parameters: TrackintelRunParameters = buildDefaultTrackintelParameters()) => {
    if (!deviceId || !rangeStartIso || !rangeEndIso) {
      return;
    }
    setRunning(true);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/rebuild`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_id: deviceId,
          range_start: rangeStartIso,
          range_end: rangeEndIso,
          include_debug_positionfixes: true,
          trackintel_dist_threshold_m: parameters.distThresholdM,
          trackintel_time_threshold_min: parameters.timeThresholdMin,
          trackintel_gap_threshold_min: parameters.gapThresholdMin,
          trackintel_merge_staypoints_enabled: parameters.mergeStaypointsEnabled,
          trackintel_merge_staypoints_max_time_gap: `${parameters.mergeStaypointsMaxTimeGapMin}min`,
        }),
      });
      if (!response.ok) {
        throw new Error(`rebuild API failed: ${response.status}`);
      }
      const payload = (await response.json()) as RebuildJob;
      setRebuildJob(payload);
      message.success('LocationAnalysis job を受け付けたよ');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'LocationAnalysis 実行に失敗しました');
    } finally {
      setRunning(false);
    }
  }, [deviceId, fetchToken, rangeEndIso, rangeStartIso]);

  useEffect(() => {
    if (!rebuildJob || !['accepted', 'running'].includes(rebuildJob.status)) {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const token = await fetchToken();
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/rebuild-jobs/${rebuildJob.job_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`rebuild job API failed: ${response.status}`);
        }
        const payload = (await response.json()) as RebuildJob;
        if (cancelled) {
          return;
        }
        setRebuildJob(payload);
        if (payload.status === 'succeeded') {
          await loadDerivedData();
          message.success('LocationAnalysis が終わったよ');
        }
        if (payload.status === 'failed') {
          message.error(payload.error_message || 'LocationAnalysis job が失敗しました');
        }
      } catch (error) {
        if (!cancelled) {
          secureLogger.error('Failed to poll location analysis rebuild job', {
            error: error instanceof Error ? error.message : 'unknown',
          });
        }
      }
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fetchToken, loadDerivedData, rebuildJob]);

  const handleRenameLocation = useCallback(async (locationId: string, label: string) => {
    const normalizedLabel = label.trim();
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          label: normalizedLabel || null,
          note: 'location analysis timeline location label edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`location update API failed: ${response.status}`);
      }
      const updatedLocation = (await response.json()) as DerivedLocation;
      setLocations((current) => {
        if (current.some((location) => location.id === updatedLocation.id)) {
          return current.map((location) =>
            location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location
          );
        }
        return [...current, updatedLocation];
      });
      setVisibleLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
      message.success('Location 名を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Location 名の更新に失敗しました');
      throw error;
    }
  }, [fetchToken]);

  const handleUpdateLocationSemanticType = useCallback(async (locationId: string, semanticType: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          semantic_type: semanticType,
          note: 'location analysis timeline location semantic type edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`location semantic update API failed: ${response.status}`);
      }
      const updatedLocation = (await response.json()) as DerivedLocation;
      setLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
      setVisibleLocations((current) =>
        current.map((location) => (location.id === updatedLocation.id ? { ...location, ...updatedLocation } : location))
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Location カテゴリの更新に失敗しました');
      throw error;
    }
  }, [fetchToken]);

  const handleSearchLocationPoi = useCallback(async (
    locationId: string,
    query: string,
    options?: { silent?: boolean }
  ) => {
    const normalizedQuery = query.trim();
    const cacheKey = `${locationId}\u0000${normalizedQuery}`;
    const cachedCandidates = poiSearchCacheRef.current.get(cacheKey);
    if (cachedCandidates) {
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.set(locationId, cachedCandidates);
        return next;
      });
      if (!options?.silent) {
        message.success(`POI候補を ${cachedCandidates.length} 件取得しました`);
      }
      return;
    }
    const requestId = (poiSearchRequestIdByLocationRef.current.get(locationId) || 0) + 1;
    poiSearchRequestIdByLocationRef.current.set(locationId, requestId);
    setSearchingPoiLocationId(locationId);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}/poi-candidates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: normalizedQuery || null,
          radius_m: 300,
          language_code: 'ja',
          max_result_count: 8,
        }),
      });
      if (!response.ok) {
        throw new Error(`POI candidates API failed: ${response.status}`);
      }
      const candidates = (await response.json()) as POICandidate[];
      poiSearchCacheRef.current.set(cacheKey, candidates);
      if (poiSearchRequestIdByLocationRef.current.get(locationId) !== requestId) {
        return;
      }
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.set(locationId, candidates);
        return next;
      });
      if (!options?.silent) {
        message.success(`POI候補を ${candidates.length} 件取得しました`);
      }
    } catch (error) {
      if (options?.silent) {
        secureLogger.warn('Incremental POI search failed', {
          locationId,
          reason: error instanceof Error ? error.message : 'unknown',
        });
      } else {
        message.error(error instanceof Error ? error.message : 'POI候補検索に失敗しました');
      }
      throw error;
    } finally {
      if (poiSearchRequestIdByLocationRef.current.get(locationId) === requestId) {
        setSearchingPoiLocationId(null);
      }
    }
  }, [fetchToken]);

  const handleLinkLocationPoi = useCallback(async (locationId: string, candidate: POICandidate) => {
    setLinkingPoiLocationId(locationId);
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/locations/${locationId}/poi-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate,
          note: 'location analysis manual Google POI link',
        }),
      });
      if (!response.ok) {
        throw new Error(`POI link API failed: ${response.status}`);
      }
      const poi = (await response.json()) as POIEnrichment;
      setLocations((current) =>
        current.map((location) =>
          location.id === locationId
            ? { ...location, label: candidate.display_name || location.label, primary_poi: poi }
            : location
        )
      );
      setVisibleLocations((current) =>
        current.map((location) =>
          location.id === locationId
            ? { ...location, label: candidate.display_name || location.label, primary_poi: poi }
            : location
        )
      );
      setPoiCandidatesByLocationId((current) => {
        const next = new Map(current);
        next.delete(locationId);
        return next;
      });
      message.success('Google POI を紐付けました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Google POI の紐付けに失敗しました');
      throw error;
    } finally {
      setLinkingPoiLocationId(null);
    }
  }, [fetchToken]);

  const handleReassignStaypointLocation = useCallback(async (
    staypointId: string,
    payload:
      | { action: 'assign'; locationId: string }
      | { action: 'create_location'; label: string | null; semanticType: string }
      | { action: 'clear' }
  ) => {
    const body =
      payload.action === 'assign'
        ? {
            staypoint_ids: [staypointId],
            action: 'assign',
            location_id: payload.locationId,
            note: 'location analysis timeline staypoint location reassignment',
          }
        : payload.action === 'create_location'
          ? {
              staypoint_ids: [staypointId],
              action: 'create_location',
              create_location: {
                label: payload.label,
                semantic_type: payload.semanticType,
              },
              note: 'location analysis timeline staypoint location split',
            }
          : {
              staypoint_ids: [staypointId],
              action: 'clear',
              note: 'location analysis timeline staypoint location override clear',
            };
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/staypoints/reassign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`staypoint reassign API failed: ${response.status}`);
      }
      await loadDerivedData();
      message.success('Staypoint のロケーション割当を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Staypoint のロケーション割当更新に失敗しました');
      throw error;
    }
  }, [fetchToken, loadDerivedData]);

  const handleOverrideTriplegMode = useCallback(async (triplegId: string, mode: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs/${triplegId}/mode`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode,
          note: 'location analysis timeline tripleg mode edit',
        }),
      });
      if (!response.ok) {
        throw new Error(`tripleg mode API failed: ${response.status}`);
      }
      await loadDerivedData();
      message.success('Tripleg の移動手段を更新しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Tripleg の移動手段更新に失敗しました');
      throw error;
    }
  }, [fetchToken, loadDerivedData]);

  const handleDeleteTripleg = useCallback(async (triplegId: string) => {
    try {
      const token = await fetchToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/internal/gnss-derived/triplegs/${triplegId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`tripleg delete API failed: ${response.status}`);
      }
      setSelectedDerivedTarget((current) =>
        current?.type === 'tripleg' && current.id === triplegId ? null : current
      );
      await loadDerivedData();
      message.success('Tripleg を削除しました');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Tripleg の削除に失敗しました');
      throw error;
    }
  }, [fetchToken, loadDerivedData]);

  const locationNameMap = useMemo(
    () =>
      new Map(
        locations.map((location) => [location.id, location.label?.trim() || `Location ${location.id.slice(0, 8)}`])
      ),
    [locations]
  );

  const locationPoiMap = useMemo(
    () =>
      new Map(
        locations
          .filter((location): location is DerivedLocation & { primary_poi: POIEnrichment } => !!location.primary_poi)
          .map((location) => [location.id, location.primary_poi])
      ),
    [locations]
  );

  const timelineItems = useMemo(
    () => buildLocationAnalysisTimelineItems(staypoints, triplegs),
    [staypoints, triplegs]
  );

  useEffect(() => {
    if (!selectedDerivedTarget) {
      return;
    }
    const targetExists =
      selectedDerivedTarget.type === 'staypoint'
        ? staypoints.some((staypoint) => staypoint.id === selectedDerivedTarget.id)
        : triplegs.some((tripleg) => tripleg.id === selectedDerivedTarget.id);
    if (!targetExists) {
      setSelectedDerivedTarget(null);
    }
  }, [selectedDerivedTarget, staypoints, triplegs]);

  const locationExtentBufferM = rebuildJob?.trackintel_dist_threshold_m ?? DEFAULT_TRACKINTEL_DIST_THRESHOLD_M;
  const rebuildJobRunning = ['accepted', 'running'].includes(rebuildJob?.status || '');

  return {
    loading,
    running,
    rebuildJobRunning,
    locations,
    visibleLocations,
    staypoints,
    triplegs,
    trips,
    selectedDerivedTarget,
    setSelectedDerivedTarget,
    poiCandidatesByLocationId,
    searchingPoiLocationId,
    linkingPoiLocationId,
    rebuildJob,
    locationNameMap,
    locationPoiMap,
    timelineItems,
    locationExtentBufferM,
    loadDerivedData,
    runAnalysis,
    handleRenameLocation,
    handleUpdateLocationSemanticType,
    handleSearchLocationPoi,
    handleLinkLocationPoi,
    handleReassignStaypointLocation,
    handleOverrideTriplegMode,
    handleDeleteTripleg,
  };
};

export default useLocationAnalysisDerivedData;
